import { useMemo, useState } from 'react';
import type { DropdownChangeEvent } from 'primereact/dropdown';
import NivoLineChart from './charts/NivoLineChart';
import { TableTooltip, Chip } from '@nivo/tooltip';
import { Dropdown } from './PrimereactComponents';
import { useTranslation } from 'react-i18next';
import './unityLineChart.css';
import { useFormatters } from '../utils/texts';
import { useLegendHoverDim } from '../utils/useLegendHoverDim';
import { buildWrappedLegends, legendMarginBottom, AXIS_TITLE_HEIGHT } from '../utils/nivoLegend';
import { sparseTickValues } from '../utils/nivoTicks';
import useElementWidth from '../hooks/useElementWidth';
import type { PartiesUnity, DropdownOptions } from '../api';

type UnityLineChartProps = {
    data: PartiesUnity;
    options: DropdownOptions[];
};

function UnityLineChart({ data, options }: UnityLineChartProps) {
    const [containerRef, containerWidth] = useElementWidth<HTMLDivElement>();
    const [selectedParty, setSelectedParty] = useState<DropdownOptions | null>(null);

    const { t, i18n } = useTranslation();
    const currentLocale = i18n.language;
    const formatter = useFormatters(currentLocale);

    const handlePartyChange = (e: DropdownChangeEvent) => {
        setSelectedParty(e.value === undefined ? null : e.value);
    };

    const datasets = selectedParty
        ? data.datasets.filter((dataset) => dataset.label === selectedParty.name)
        : data.datasets;

    const colorMap = useMemo(
        () => Object.fromEntries(data.datasets.map((dataset) => [dataset.label, dataset.borderColor[0]])),
        [data.datasets]
    );
    const { colors, legends } = useLegendHoverDim(colorMap);

    const definedValues = data.datasets.flatMap((dataset) =>
        dataset.data.filter((v): v is number => v !== null && v !== undefined)
    );
    const max = definedValues.length > 0 ? Math.max(...definedValues) : 100;

    const series = datasets.map((dataset) => ({
        id: dataset.label,
        data: data.labels.map((label, i) => ({ x: label, y: dataset.data[i] ?? null }))
    }));

    // Many meetings render one x-axis tick per label by default, which overlaps once
    // there are more than a handful (Nivo has no Chart.js-style tick auto-skip) -
    // thin them out and angle the survivors, same as a rotated/skipped Chart.js axis.
    const manyMeetings = data.labels.length > 15;
    // xTitle is always set below, so the legend needs to start after both the tick
    // labels/axis title zone to avoid overlapping either.
    const bottomAxisStartY = (manyMeetings ? 100 : 55) + AXIS_TITLE_HEIGHT;
    const { legends: legendRows, rowCount: legendRowCount } = buildWrappedLegends(
        datasets.map((dataset) => ({ id: dataset.label, label: dataset.label, color: colorMap[dataset.label] })),
        {
            containerWidth: containerWidth || undefined,
            itemWidth: 170,
            startY: bottomAxisStartY,
            onMouseEnter: legends.onMouseEnter,
            onMouseLeave: legends.onMouseLeave
        }
    );

    return (
        <div className='unity-line-chart'>
            <div className='selection-wrapper'>
                <span className='p-float-label'>
                    <Dropdown
                        showClear
                        inputId='partySelection'
                        value={selectedParty}
                        options={options}
                        onChange={handlePartyChange}
                        optionLabel='name'
                    />
                    <label htmlFor='partySelection'>{t('selectParty', { ns: 'parties' })}</label>
                </span>
            </div>
            <div ref={containerRef}>
                <NivoLineChart
                    height='450px'
                    yTitle={t('UnityLineChart.disunity', { ns: 'translation' })}
                    xTitle={t('meetings', { ns: 'meetings' })}
                    data={series}
                    colors={colors}
                    lineWidth={2}
                    pointSize={0}
                    yScale={{ type: 'linear', min: 0, max: Math.ceil(max) }}
                    axisLeft={{ format: (value) => formatter.percentFrom100(Number(value)) }}
                    axisBottom={
                        manyMeetings
                            ? { tickValues: sparseTickValues(data.labels, 15), tickRotation: -40, legendOffset: 70 }
                            : undefined
                    }
                    margin={{
                        top: 20,
                        right: 20,
                        bottom: legendMarginBottom(legendRowCount, { startY: bottomAxisStartY }),
                        left: manyMeetings ? 90 : 60
                    }}
                    enableSlices='x'
                    legends={legendRows}
                    sliceTooltip={({ slice }) => (
                        <TableTooltip
                            title={<strong>{t('meetingNo', { ns: 'meetings', num: slice.points[0]?.data.x })}</strong>}
                            rows={slice.points.map((point) => [
                                <Chip key='chip' color={point.seriesColor} />,
                                point.seriesId,
                                formatter.percent2From100(Number(point.data.y))
                            ])}
                        />
                    )}
                />
            </div>
        </div>
    );
}
export default UnityLineChart;
